import { RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import type { CloudPromptVersion } from '../types/cloud';

interface VersionListProps {
  versions: CloudPromptVersion[];
  isLoading: boolean;
  error: string | null;
  onViewVersion: (version: CloudPromptVersion) => void;
  onRetry: () => void;
}

function formatVersionDate(createdAt: string): string {
  return new Date(createdAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function VersionList({
  versions,
  isLoading,
  error,
  onViewVersion,
  onRetry,
}: Readonly<VersionListProps>): JSX.Element {
  if (isLoading) {
    return (
      <div className="space-y-2" aria-label="Loading versions">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className="animate-pulse rounded-md border border-border/70 bg-muted/30 p-3"
          >
            <div className="mb-2 h-3 w-24 rounded bg-muted" />
            <div className="mb-2 h-3 w-40 rounded bg-muted/80" />
            <div className="h-3 w-32 rounded bg-muted/70" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
        <div className="mb-2 text-sm font-medium text-destructive">Error loading versions</div>
        <p className="m-0 mb-3 text-xs text-destructive/90">{error}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="rounded-md border border-border/70 bg-muted/20 p-3">
        <p className="m-0 text-sm text-muted-foreground">No versions found.</p>
      </div>
    );
  }

  return (
    <div className="max-h-[340px] space-y-2 overflow-y-auto pr-1" aria-label="Version history list">
      {versions.map((version) => (
        <div key={version.id} className="rounded-md border border-border/70 bg-card p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="mb-1 flex items-center gap-2">
                {version.isCurrent ? (
                  <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                    Current
                  </span>
                ) : null}
                <p className="m-0 truncate text-sm font-medium text-foreground">"{version.title}"</p>
              </div>
              <p className="m-0 text-xs text-muted-foreground">
                {version.editedByName || version.editedBy}
              </p>
              <p className="m-0 text-xs text-muted-foreground">{formatVersionDate(version.createdAt)}</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => onViewVersion(version)}>
              View
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
